import { Form } from 'antd';
import React, { FC, memo, useCallback, useMemo, useState } from 'react';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { useAppDispatch, useAppSelector } from 'app/config/storeConfig/hooks';
import { getSelectedWarehouse } from 'widgets/IssueInvoiceModal/model/selectors/getSelectedWarehouse';
import { getSelectedUser } from 'widgets/IssueInvoiceModal/model/selectors/getSelectedUser';
import { BulletsTable, TableRow } from 'shared/ui/BulletsTable';
import { getUserName } from 'entities/User';
import { stepperModalActions } from 'features/StepperModal';
import { issueInvoiceModalActions } from 'widgets/IssueInvoiceModal/model/slice/issueInvoiceModalSlice';
import { Button } from 'shared/ui/Button';
import { getInvoiceData } from 'widgets/IssueInvoiceModal/model/selectors/getInvoiceData';
import { ReactComponent as PlusIcon } from 'shared/assets/icons/PlusIcon.svg';
import { ReactComponent as EditIcon } from 'shared/assets/icons/EditIcon.svg';
import { getClientDateFormat } from 'shared/utils/helpers/getDateFormat';
import dayjs from 'dayjs';
import { roundNumber } from 'shared/utils/helpers/roundNumber';
import { useGetCurrencySymbol } from 'app/appState';
import { InvoiceItem } from 'entities/Invoice';
import { InoviceItemModal } from './InoviceItemModal';

export const InvoiceItemsStep: FC = memo(() => {
  const { t } = useAppTranslation(['contracts', 'booking', 'common']);

  const currencySymbol = useGetCurrencySymbol();
  const selectedWarehouse = useAppSelector(getSelectedWarehouse);
  const selectedUser = useAppSelector(getSelectedUser);
  const invoiceData = useAppSelector(getInvoiceData);
  const dispatch = useAppDispatch();

  const [form] = Form.useForm();

  const [isOpened, setOpened] = useState(false);
  const [editingItem, setEditingItem] = useState<Nullable<Partial<InvoiceItem>>>(null);
  const [editingIndex, setEditingIndex] = useState<Nullable<number>>(null);

  const openAddModal = useCallback(() => {
    setEditingItem(null);
    setEditingIndex(null);
    setOpened(true);
  }, []);

  const openEditModal = useCallback((item: InvoiceItem, index: number) => {
    setEditingItem(item);
    setEditingIndex(index);
    setOpened(true);
  }, []);

  const closeModal = useCallback(() => {
    setOpened(false);
  }, []);

  const invoiceDetailsBullets = useMemo(() => {
    const bullets: TableRow[] = [
      { label: t('Type of Invoice'), value: t('Personal invoice') },
      { label: t('Warehouse'), value: selectedWarehouse?.name },
      { label: t('Customer'), value: getUserName(selectedUser) },
      { label: t('Email'), value: invoiceData?.invoiceEmail },
      { label: t('Address'), value: invoiceData?.invoiceAddress },
      { label: t('Due date'), value: getClientDateFormat(invoiceData?.dueDate || dayjs().startOf('day')) },
    ];

    return bullets;
  }, [t, selectedWarehouse?.name, selectedUser, invoiceData?.invoiceEmail, invoiceData?.invoiceAddress, invoiceData?.dueDate]);

  const goToNextStep = useCallback(
    ({ invoiceItems }: { invoiceItems: InvoiceItem[] }): void => {
      const invoiceAmount = roundNumber(invoiceItems.reduce((sum: number, item: InvoiceItem) => sum + (item.totalPrice || 0), 0));

      dispatch(stepperModalActions.changeActiveStepIndex(4));
      dispatch(issueInvoiceModalActions.setInvoiceItems(invoiceItems));
      dispatch(issueInvoiceModalActions.setInvoiceData({ ...invoiceData, amount: invoiceAmount }));
    },
    [dispatch, invoiceData],
  );

  const handleItemModalSuccess = useCallback(
    (values: Partial<InvoiceItem>, add: (value: Partial<InvoiceItem>) => void) => {
      const items = form.getFieldValue('invoiceItems') || [];
      const newItems = Array.isArray(items) ? [...items] : [];

      if (editingIndex !== null && typeof editingIndex === 'number') {
        newItems[editingIndex] = values;
        form.setFieldsValue({ invoiceItems: newItems });
      } else {
        add(values);
      }
    },
    [form, editingIndex],
  );

  return (
    <Form className="flex flex-col p-4" form={form} layout="vertical" onFinish={goToNextStep}>
      <div className="flex justify-between mb-4">
        <div className="font-semibold text-2xl mb-4">{t('Invoice items')}</div>
        <Button icon={<PlusIcon className="[&>path]:stroke-white" />} iconPosition="prev" containerClassName="w-fit" onClick={openAddModal}>
          {t('Add position')}
        </Button>
      </div>

      <div className="flex gap-10">
        <div className="flex-1">
          <Form.List name="invoiceItems">
            {(fields, { add, remove }) => {
              const invoiceItems = form.getFieldValue('invoiceItems') || [];
              const totalPrice = roundNumber(invoiceItems.reduce((sum: number, item: InvoiceItem) => sum + (item.totalPrice || 0), 0));

              return (
                <>
                  {fields.map(({ key, name }) => {
                    const item = form.getFieldValue(['invoiceItems', name]);

                    return (
                      <div key={key} className="border border-border rounded-md p-4 flex justify-between items-center mb-3">
                        <div>
                          <div className="flex gap-2 font-medium">
                            <div>{t(item?.invoiceItemType, { ns: 'common' })}</div>
                            <div>
                              {item?.quantity} × {roundNumber(item?.totalPrice / item?.quantity)} {currencySymbol}
                            </div>
                          </div>
                          <div className="text-primaryLight">{item?.description}</div>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            theme="secondary"
                            size="small"
                            className="!p-[5px]"
                            icon={<EditIcon />}
                            onClick={() => {
                              openEditModal(item, name);
                            }}
                          />
                          <Button
                            size="small"
                            theme="danger"
                            fontSize="small"
                            className="!px-[10px] !py-[6px]"
                            onClick={() => {
                              remove(name);
                            }}
                          >
                            ✕
                          </Button>
                        </div>
                      </div>
                    );
                  })}

                  <InoviceItemModal
                    isOpened={isOpened}
                    onClose={closeModal}
                    invoiceItem={editingItem}
                    onSuccess={(values) => {
                      handleItemModalSuccess(values, add);
                    }}
                  />
                  {totalPrice > 0 && (
                    <div className="font-semibold text-lg flex justify-end mt-4">
                      {t('Total')}: {totalPrice} {currencySymbol}
                    </div>
                  )}
                </>
              );
            }}
          </Form.List>
        </div>
        <div className="flex-1 gap-4 flex flex-col">
          <div className="flex flex-col bg-secondaryLight rounded-lg">
            <BulletsTable heading={t('Preview Invoice Details')} rows={invoiceDetailsBullets} labelClassName="max-w-[220px]" />
          </div>
        </div>
      </div>

      <Form.Item shouldUpdate={(prevValues, currentValues) => prevValues.invoiceItems !== currentValues.invoiceItems}>
        {({ getFieldValue }) => {
          const invoiceItems = getFieldValue('invoiceItems') || [];
          return (
            <div className="flex justify-end mt-4">
              <Button type="submit" isDisabled={invoiceItems.length === 0}>
                {t('Next')}
              </Button>
            </div>
          );
        }}
      </Form.Item>
    </Form>
  );
});
